"use client";

import { useState, useEffect, useRef } from "react";
import { Pause, Loader2, Play } from "lucide-react";
import {
  trackUpgradeEntrypointViewed,
  trackUpgradeEntrypointClicked,
} from "@/lib/analytics";

interface PauseStatus {
  paused: boolean;
  resumesAt?: string;
}

export function PausedBanner() {
  const [status, setStatus] = useState<PauseStatus | null>(null);
  const [resuming, setResuming] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/cancel/pause-status")
      .then((r) => r.json())
      .then((d) => setStatus(d))
      .catch(() => {});
  }, []);

  const isVisible = !!status?.paused && !resumed;

  // Fire the impression once, when the paused banner first becomes visible.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || viewedRef.current) return;
    viewedRef.current = true;
    trackUpgradeEntrypointViewed({
      entry_point: "paused_banner",
      surface: "workspace",
    });
  }, [isVisible]);

  if (!status?.paused || resumed) return null;

  const resumesAt = status.resumesAt
    ? new Date(status.resumesAt).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  async function handleUnpause() {
    trackUpgradeEntrypointClicked({
      entry_point: "paused_banner",
      surface: "workspace",
    });
    setResuming(true);
    setError(null);

    try {
      const res = await fetch("/api/cancel/unpause", { method: "POST" });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to resume");
        setResuming(false);
        return;
      }

      setResumed(true);
    } catch {
      setError("Something went wrong.");
    } finally {
      setResuming(false);
    }
  }

  return (
    <div className="w-full border-b border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <Pause className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-semibold">Your subscription is paused</span>
            {resumesAt && (
              <span className="hidden sm:inline">
                {" "}
                &mdash; resumes automatically on {resumesAt}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {error && <p className="text-xs text-red-500">{error}</p>}
          <button
            onClick={handleUnpause}
            disabled={resuming}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-amber-700 disabled:opacity-40 dark:bg-amber-500 dark:hover:bg-amber-600"
          >
            {resuming ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <Play className="h-3 w-3" />
                Resume now
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
